import { useMemo, useState } from 'react'
import { Betrag } from './Betrag'
import { api, periodenname } from './api'
import { belegtext, t, anzahlwort } from './sprache'
import { Zustand } from './Zustand'
import { Zuordnungsfenster } from './Zuordnungsfenster'
import type { Importvorschau, Packagebericht, Packagevorschau } from './typen'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import { useDarf } from './Rechte'

/**
 * Die Blattliste eines Reporting Packages — und der eine Knopf, der es bucht
 * (5.1 Abschnitt 3.26, R743).
 *
 * **Der erste Schritt schreibt nichts.** Was hier steht, ist gelesen und
 * geprüft: je Blatt die Zeilen, die Probe und was ihm entgegensteht. Darunter
 * stehen die Proben, die über die Blattgrenze hinweg rechnen — Intercompany
 * gegen die Financials, der Spiegel gegen dieselbe Saldenliste.
 *
 * **Alles oder nichts.** Ein Package ist ein Gegenstand: Geht eine Probe nicht
 * auf, bleibt der Knopf gesperrt und sagt warum. Er bucht dann die ganze
 * Mappe in einem Zug — Anfangsbestand, Saldenliste, Umgliederungen.
 */
export function Packageliste({
  vorschau,
  einheit,
  laeuft,
  aufNeuPruefen,
  aufFertig,
}: {
  vorschau: Packagevorschau
  einheit: string
  laeuft: boolean
  /** Nach dem Zuordnen wird die ganze Mappe neu gelesen (R592, R729). */
  aufNeuPruefen: () => void
  /** Gebucht — mit den Läufen der Monate, die die Liste danach markiert (7.2 Abschnitt 35, R1136). */
  aufFertig: (laeufe: number[]) => void
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('package:packageliste')

  const darf = useDarf()
  const [fensterOffen, setFensterOffen] = useState(false)
  const [bericht, setBericht] = useState<Packagebericht | null>(null)
  const [bucht, setBucht] = useState(false)

  const gelesen = useMemo(
    () => vorschau.blaetter.filter((b) => b.vorschau !== null),
    [vorschau],
  )

  const offen = useMemo(
    () => gelesen.reduce((summe, b) => summe + (b.vorschau?.fehlerhaft ?? 0), 0),
    [gelesen],
  )

  /*
    **Eine Vorschau über alle Blätter** — nur für das Zuordnungsfenster (R729).
    Sie wird nie gebucht: Gebucht werden die einzelnen Blätter, jedes in seine
    Periode. Hier steht sie, damit ein Konto, das in Financials und Spiegel
    offen ist, einmal zugeordnet wird und nicht zweimal.
  */
  const gebuendelt: Importvorschau | null = useMemo(() => {
    if (gelesen.length === 0) {
      return null
    }

    return {
      ...gelesen[0]!.vorschau!,
      dateiname: vorschau.dateiname,
      zeilen: gelesen.flatMap((b) => b.vorschau!.zeilen),
    }
  }, [gelesen, vorschau.dateiname])

  /**
   * Buchen heißt hier: **einen Knopf drücken** (R749).
   *
   * Die Mappe liegt seit der Prüfung im Archiv; was reist, ist ihre Nummer.
   * Der Dienst liest sie Blatt für Blatt neu, während er bucht — für den
   * Anwender ist das nichts, was er noch einmal tun müsste.
   */
  async function uebernehmen() {
    if (vorschau.rohdatei === null) {
      return
    }

    setBucht(true)
    try {
      const ergebnis = await api.packageUebernehmen(vorschau.rohdatei, einheit, vorschau.quelle)
      setBericht(ergebnis)

      // Das Kästchen sagt „Package erfolgreich gespeichert", ohne die Zahl der
      // Lieferbelege (7.2 Abschnitt 34, R1131); die Liste zeigt sie (R1136).
      if (!ergebnis.halt) {
        aufFertig(ergebnis.gebucht.map((g) => g.lauf))
      }
    } finally {
      setBucht(false)
    }
  }

  /*
    **Das Hindernis steht allein** (R144): Eine Mappe ohne Deckblatt oder ohne
    lesbare Periode ist keine halbe Lieferung — eine Tabelle darunter
    beantwortete keine Frage, die dann noch offen wäre.
  */
  if (vorschau.hindernis) {
    return (
      <div className="abschnitt">
        <h2>{t('import.package.titel')}</h2>
        <p className="hinweis">{vorschau.hindernis}</p>
      </div>
    )
  }

  return (
    <div className="abschnitt">
      <h2>{t('import.package.titel')}</h2>

      <div className={fensterOffen ? 'kennzahlen veraltet' : 'kennzahlen'}>
        <div className="kennzahl">
          <span className="name">{t('kontext.periode')}</span>
          <span className="wert">{periodenname(vorschau.periode)}</span>
        </div>
        <div className="kennzahl">
          <span className="name">{t('import.package.blaetter')}</span>
          <span className="wert">{anzahlwort(gelesen.length)}</span>
        </div>
        <div className="kennzahl">
          <span className="name">{t('import.package.eroeffnung')}</span>
          <span className="wert">
            {vorschau.eroeffnungWirdGebucht
              ? t('import.package.eroeffnung.gebucht')
              : t('import.package.eroeffnung.verprobt')}
          </span>
        </div>
        <div className="kennzahl">
          <span className="name">{t('import.vorschau.probe')}</span>
          <span className="wert">
            <Zustand
              art={vorschau.tragfaehig ? 'fertig' : 'offen'}
              text={
                vorschau.tragfaehig
                  ? t('import.package.bereit')
                  : t('import.package.befunde', { anzahl: anzahlwort(vorschau.proben.length + offen) })
              }
            />
          </span>
        </div>
      </div>

      {fensterOffen && <p className="hinweis">{t('import.zuordnung.veraltet')}</p>}

      <p className="handreichung">
        {darf('zuordnen') && offen > 0 && gebuendelt && (
          <button type="button" disabled={laeuft || bucht} onClick={() => setFensterOffen(true)}>
            {t(offen === 1 ? 'import.zuordnung.oeffnen.eins' : 'import.zuordnung.oeffnen.mehrere', {
              anzahl: anzahlwort(offen),
            })}
          </button>
        )}{' '}
        {/* Wer nicht buchen darf, sieht den Knopf nicht und auch nicht, warum er gesperrt wäre (R1180). */}
        {darf('packageUebernehmen') && (
          <>
            <button
              type="button"
              className="haupt"
              disabled={laeuft || bucht || !vorschau.tragfaehig || vorschau.rohdatei === null}
              onClick={() => void uebernehmen()}
            >
              {t(bucht ? 'import.package.buchtgerade' : 'import.package.uebernehmen')}
            </button>

            {/* Nur der gesperrte Knopf sagt warum (R144). */}
            {!vorschau.tragfaehig && (
              <span className="erlaeuterung"> {t('import.package.gesperrt')}</span>
            )}
          </>
        )}
      </p>

      {/*
        **Nur der Abbruch steht hier** (7.2 Abschnitt 34, R1132): Ein ganz
        gebuchtes Package geht in die Liste, und das Kästchen sagt es. Der Grund
        des Abbruchs ist ein Satz des Rechenkerns und wird ohne Regelnummer
        gelesen (R1133).
      */}
      {bericht?.halt && (
        <div className={'hinweis'}>
          <p>
            {t('import.package.bericht.halt', {
              gebucht: anzahlwort(bericht.gebucht.length),
              blatt: bericht.blatt ?? '—',
            })}
          </p>
          <p>{belegtext(bericht.halt)}</p>
        </div>
      )}

      <div className="tabellenrahmen">
        <table ref={spaltenrahmen} className="ziehbaretabelle">
          <Spaltenmassleiste masse={spaltenmasse} />
          <thead>
            <tr>
              <th>{t('import.package.spalte.blatt')}</th>
              <th>{t('import.package.spalte.wirkung')}</th>
              <th className="zahl">{t('import.spalte.gelesen')}</th>
              <th className="zahl">{t('import.stapel.spalte.summe')}</th>
              <th>{t('import.vorschau.probe')}</th>
              <th>{t('import.spalte.befund')}</th>
            </tr>
          </thead>
          <tbody>
            {vorschau.blaetter.map((b) => (
              <tr key={b.blatt} className={b.befund ? 'geaendert' : undefined}>
                <td className="code">{b.blatt}</td>
                <td>{t(`import.package.rolle.${b.rolle}`)}</td>
                <td className="zahl">{b.vorschau ? anzahlwort(b.vorschau.gelesen) : '—'}</td>
                <td className="zahl">{b.vorschau ? <Betrag wert={b.vorschau.summe} /> : '—'}</td>
                <td>
                  {b.vorschau && (
                    <Zustand
                      art={b.befund === null && b.vorschau.gehtAuf ? 'fertig' : 'blockiert'}
                      text={
                        b.befund === null && b.vorschau.gehtAuf
                          ? b.bucht
                            ? t('import.vorschau.gehtauf')
                            : t('import.package.nurverprobt')
                          : t('import.package.befund')
                      }
                    />
                  )}
                </td>
                <td>{b.befund ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/*
        **Die Proben über die Blattgrenze stehen unter der Liste** und nicht in
        ihr: Sie gehören zu keinem einzelnen Blatt, sondern zu zweien — der
        Spiegel zur Saldenliste, das Intercompany-Blatt zu denselben Konten.

        **Die Sätze kommen fertig vom Rechenkern und lesen sich mit Monat und
        Jahr** (7.2 Abschnitt 21, R839 — Befund vom 2026-09-29): Eine Periode
        stand darin als „202612" oder „12/2026". Das gilt für die Proben wie
        für die Meldungen darunter.
      */}
      {vorschau.proben.length > 0 && (
        <div className="hinweis">
          <p>{t('import.package.proben', { anzahl: anzahlwort(vorschau.proben.length) })}</p>
          <ul>
            {vorschau.proben.map((p) => (
              <li key={p}>{belegtext(p)}</li>
            ))}
          </ul>
        </div>
      )}

      {vorschau.meldungen.length > 0 && (
        <div className="hinweis">
          <ul>
            {vorschau.meldungen.map((m) => (
              <li key={m}>{belegtext(m)}</li>
            ))}
          </ul>
        </div>
      )}

      {fensterOffen && gebuendelt && (
        <Zuordnungsfenster
          vorschau={gebuendelt}
          einheit={einheit}
          periode={vorschau.periode}
          aufSchliessen={(geschrieben) => {
            setFensterOffen(false)
            if (geschrieben) {
              aufNeuPruefen()
            }
          }}
        />
      )}
    </div>
  )
}
